import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink, MapPin, Tag } from 'lucide-react';
import { useToast } from './ToastContainer';
import api from '../utils/api';
import './Shopify.css';

const moneyFmt = (v, code) => {
  if (v == null) return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: code || 'USD', maximumFractionDigits: 2,
    }).format(Number(v));
  } catch { return `${code || ''} ${Number(v).toLocaleString()}`; }
};
const dateFmt = (iso) => iso ? new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const ShopifyOrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showError } = useToast();
  const [order, setOrder] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/api/shopify/orders/${id}`)
      .then(r => { setOrder(r.data.order); setCustomer(r.data.customer); })
      .catch(e => showError('Failed to load: ' + (e.response?.data?.error || e.message)))
      .finally(() => setLoading(false));
  }, [id, showError]);

  if (loading) return (
    <div className="shop-loading"><div className="shop-spinner" /></div>
  );
  if (!order) return (
    <div className="shop"><div className="shop-empty">Order not found.</div></div>
  );

  const cur = order.currency_code;

  return (
    <div className="shop">
      <button onClick={() => navigate(-1)} className="shop-action-btn" style={{ marginBottom: '1rem' }}>
        <ArrowLeft size={14} /> Back
      </button>

      <div className="shop-header">
        <div>
          <h1 className="shop-title">{order.name}</h1>
          <p className="shop-subtitle">
            Placed {dateFmt(order.shopify_created_at)}
            {order.cancelled_at && <> · <span style={{ color: '#f87171' }}>Cancelled {dateFmt(order.cancelled_at)}</span></>}
          </p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div className="shop-kpi-value" style={{ fontSize: '1.5rem' }}>{moneyFmt(order.total_price, cur)}</div>
          <div style={{ marginTop: '0.4rem', display: 'flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
            <span className={`shop-badge ${(order.display_financial_status || '').toLowerCase()}`}>{order.display_financial_status}</span>
            <span className={`shop-badge ${(order.display_fulfillment_status || '').toLowerCase()}`}>{order.display_fulfillment_status}</span>
          </div>
        </div>
      </div>

      {/* Money breakdown */}
      <div className="shop-panel">
        <div className="shop-panel-head"><h3>Order totals</h3></div>
        <table className="shop-table">
          <tbody>
            <tr><td>Subtotal</td><td style={{ textAlign: 'right' }}>{moneyFmt(order.subtotal_price, cur)}</td></tr>
            <tr><td>Tax</td><td style={{ textAlign: 'right' }}>{moneyFmt(order.total_tax, cur)}</td></tr>
            <tr><td>Shipping</td><td style={{ textAlign: 'right' }}>{moneyFmt(order.total_shipping, cur)}</td></tr>
            <tr><td>Discounts</td><td style={{ textAlign: 'right', color: '#fbbf24' }}>−{moneyFmt(order.total_discounts, cur)}</td></tr>
            <tr style={{ fontWeight: 700 }}><td>Total</td><td style={{ textAlign: 'right' }}>{moneyFmt(order.total_price, cur)}</td></tr>
            {order.total_refunded > 0 && (
              <tr><td style={{ color: '#f87171' }}>Refunded</td><td style={{ textAlign: 'right', color: '#f87171' }}>{moneyFmt(order.total_refunded, cur)}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Line items */}
      <div className="shop-panel">
        <div className="shop-panel-head">
          <h3>Line items</h3>
          <span className="shop-panel-meta">{(order.line_items || []).length} items</span>
        </div>
        {(order.line_items || []).length === 0 ? (
          <div className="shop-empty">No line items.</div>
        ) : (
          <table className="shop-table">
            <thead>
              <tr><th>Product</th><th>SKU</th><th>Qty</th><th style={{ textAlign: 'right' }}>Total</th></tr>
            </thead>
            <tbody>
              {order.line_items.map(li => (
                <tr key={li.id}>
                  <td>{li.title}</td>
                  <td style={{ color: 'rgba(255,255,255,0.6)', fontFamily: 'monospace', fontSize: '0.78rem' }}>{li.sku || '—'}</td>
                  <td>{li.quantity}</td>
                  <td style={{ textAlign: 'right' }}>{moneyFmt(li.discounted_total ?? li.original_total, cur)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Customer card */}
      {customer && (
        <div className="shop-panel">
          <div className="shop-panel-head">
            <h3>Customer</h3>
            <Link to={`/shopify/customers/${customer.id}`} className="shop-panel-link">View profile <ExternalLink size={12} /></Link>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', fontSize: '0.85rem' }}>
            <div>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Name</div>
              <div style={{ marginTop: '0.25rem', fontWeight: 600 }}>{customer.display_name || '—'}</div>
            </div>
            <div>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Lifetime Spent</div>
              <div style={{ marginTop: '0.25rem', fontWeight: 600, color: '#10b981' }}>{moneyFmt(customer.total_spent_amount, customer.total_spent_currency)}</div>
            </div>
            <div>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Total Orders</div>
              <div style={{ marginTop: '0.25rem', fontWeight: 600 }}>{customer.total_orders}</div>
            </div>
            <div>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>VIP Tier</div>
              <div style={{ marginTop: '0.25rem', fontWeight: 600, color: tierColor(customer.vip_tier) }}>{customer.vip_tier || '—'}</div>
            </div>
          </div>
        </div>
      )}

      {/* Refunds */}
      {(order.refunds || []).length > 0 && (
        <div className="shop-panel">
          <div className="shop-panel-head"><h3>Refunds</h3></div>
          <table className="shop-table">
            <thead><tr><th>When</th><th>Note</th><th style={{ textAlign: 'right' }}>Amount</th></tr></thead>
            <tbody>
              {order.refunds.map(r => (
                <tr key={r.id}>
                  <td>{dateFmt(r.shopify_created_at)}</td>
                  <td>{r.note || '—'}</td>
                  <td style={{ textAlign: 'right', color: '#f87171' }}>{moneyFmt(r.total_refunded, r.currency_code || cur)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Fulfillments */}
      {(order.fulfillments || []).length > 0 && (
        <div className="shop-panel">
          <div className="shop-panel-head"><h3>Fulfillments</h3></div>
          <table className="shop-table">
            <thead><tr><th>Status</th><th>Carrier</th><th>Tracking</th><th>Created</th></tr></thead>
            <tbody>
              {order.fulfillments.map(f => (
                <tr key={f.id}>
                  <td><span className={`shop-badge ${(f.status || '').toLowerCase()}`}>{f.status}</span></td>
                  <td>{f.tracking_company || '—'}</td>
                  <td>{f.tracking_number ? (f.tracking_url ? <a href={f.tracking_url} target="_blank" rel="noreferrer" className="shop-order-link">{f.tracking_number}</a> : f.tracking_number) : '—'}</td>
                  <td>{dateFmt(f.shopify_created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Address + tags */}
      <div className="shop-panel">
        <div className="shop-panel-head"><h3>Shipping</h3></div>
        <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', fontSize: '0.85rem', color: 'rgba(255,255,255,0.85)' }}>
          {order.shipping_address && (
            <div>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem', textTransform: 'uppercase', marginBottom: '0.4rem' }}><MapPin size={12} style={{ verticalAlign: 'middle' }} /> Address</div>
              <div>{order.shipping_address.address1 || ''}</div>
              <div>{[order.shipping_address.city, order.shipping_address.province, order.shipping_address.country].filter(Boolean).join(', ')}</div>
            </div>
          )}
          {(order.tags || []).length > 0 && (
            <div>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem', textTransform: 'uppercase', marginBottom: '0.4rem' }}><Tag size={12} style={{ verticalAlign: 'middle' }} /> Tags</div>
              <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                {order.tags.map(t => <span key={t} className="shop-badge">{t}</span>)}
              </div>
            </div>
          )}
          {order.note && (
            <div>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem', textTransform: 'uppercase', marginBottom: '0.4rem' }}>Note</div>
              <div>{order.note}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const tierColor = (t) => ({ Gold: '#fbbf24', Silver: '#cbd5e1', Bronze: '#d97706' }[t] || '#fff');

export default ShopifyOrderDetail;
